import { useState, type ReactNode, type RefObject } from "react"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { AddDashedTile } from "@/components/ui/add-dashed-tile"
import { ReferenceInputThumb } from "@/components/reference-inputs/reference-input-thumb"
import { useTranslation } from "@/hooks/use-translation"
import { isCanvasDragType, readCanvasReferenceFromDrop } from "@/lib/canvas-drag"
import { isFileDrag } from "@/lib/filter-files-by-accept"
import { filterReferenceFilesByMode, type ReferenceFileAcceptMode } from "@/lib/reference-file-policy"
import { cn } from "@/lib/utils"
import type { CanvasReferenceItem } from "@/types/canvas"

export type ReferenceStripItem = {
  key: string
  url: string | null
  thumbUrl?: string | null
  /** Full original for lightbox / sketch. */
  downloadUrl?: string | null
  /** false — смотреть превью можно, скачивать файл нельзя. */
  downloadable?: boolean
  mediaType?: "image" | "video" | "audio"
  label?: string
  /** Built-in ref kept on the strip but left out of this generation. */
  muted?: boolean
  /** Eye toggle instead of the remove cross. The thumb stays; `enabled: false` grays it. */
  disableToggle?: boolean
  /** With `disableToggle`: false grays the thumb and leaves it out of the run. */
  enabled?: boolean
}

type ReferenceStripProps = {
  references: ReferenceStripItem[]
  /** Required for audio preview in SketchDialog. */
  projectId?: number
  /** wrap (default): wrap on md+. scroll: always one row with horizontal overflow. */
  layout?: "wrap" | "scroll"
  onRemove?: (index: number) => void
  /** Built-in refs: toggle `enabled` instead of removing the thumb. */
  onToggleEnabled?: (index: number) => void
  visibilityOnAriaLabel?: string
  visibilityOffAriaLabel?: string
  onAddClick?: () => void
  showAddTile?: boolean
  onApply?: (index: number) => void
  applyTitle?: string
  onSketchEdit?: (index: number) => void
  /** Image click: host opens sketch view. Video / no callback still use lightbox. */
  onOpenPreview?: (index: number) => void
  sketchEditingKey?: string | null
  onLabelClick?: (index: number) => void
  onInsertMention?: (label: string, index: number) => void
  onDropReference?: (ref: CanvasReferenceItem) => void
  onDropFiles?: (files: File[]) => void | Promise<void>
  fileAcceptMode?: ReferenceFileAcceptMode
  onDropRejected?: () => void
  fileDropDisabled?: boolean
  adding?: boolean
  referencePickerOpen?: boolean
  addButtonRef?: RefObject<HTMLButtonElement | null>
  allowDragDrop?: boolean
  ariaLabel?: string
  /** Плитки в том же ряду, что референсы. Свою кнопку «добавить» полоса при этом не рисует. */
  trailing?: ReactNode
}

export function referenceStripItemFromSigned(item: {
  signed_id: string
  url: string
  thumb_url?: string | null
  download_url?: string | null
}): ReferenceStripItem {
  return {
    key: item.signed_id,
    url: item.url,
    thumbUrl: item.thumb_url,
    downloadUrl: item.download_url,
    mediaType: "image",
  }
}

function lightboxViewUrl(item: ReferenceStripItem): string | null {
  return item.downloadUrl ?? item.url ?? null
}

function lightboxDownloadUrl(item: ReferenceStripItem): string | null {
  if (item.downloadable === false) return null
  return item.downloadUrl ?? item.url ?? null
}

export function ReferenceStrip({
  references,
  projectId,
  layout = "wrap",
  onRemove,
  onToggleEnabled,
  visibilityOnAriaLabel,
  visibilityOffAriaLabel,
  onAddClick,
  showAddTile = true,
  onApply,
  applyTitle,
  onSketchEdit,
  onOpenPreview,
  sketchEditingKey,
  onLabelClick,
  onInsertMention,
  onDropReference,
  onDropFiles,
  fileAcceptMode = "image",
  onDropRejected,
  fileDropDisabled = false,
  adding = false,
  referencePickerOpen = false,
  addButtonRef,
  allowDragDrop = false,
  ariaLabel,
  trailing,
}: ReferenceStripProps) {
  const { t } = useTranslation("workspace")
  const [referenceDragOver, setReferenceDragOver] = useState(false)
  const [fileDragOver, setFileDragOver] = useState(false)
  const [lightbox, setLightbox] = useState<ReferenceStripItem | null>(null)
  const scrollLayout = layout === "scroll"

  const fileDropEnabled = onDropFiles != null && !fileDropDisabled
  const highlightDrag = referenceDragOver || fileDragOver

  const clearDragState = () => {
    setReferenceDragOver(false)
    setFileDragOver(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    if (fileDropEnabled && isFileDrag(e.dataTransfer)) {
      e.preventDefault()
      e.dataTransfer.dropEffect = "copy"
      setFileDragOver(true)
      setReferenceDragOver(false)
      return
    }

    if (!allowDragDrop || !isCanvasDragType(e.dataTransfer)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "copy"
    setReferenceDragOver(true)
    setFileDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    const wasFileDrag = fileDragOver
    clearDragState()

    if (fileDropEnabled && (wasFileDrag || e.dataTransfer.files.length > 0)) {
      const dropped = Array.from(e.dataTransfer.files)
      const batch = filterReferenceFilesByMode(dropped, fileAcceptMode)
      if (batch.length === 0) {
        if (dropped.length > 0) onDropRejected?.()
        return
      }
      e.preventDefault()
      void onDropFiles(batch)
      return
    }

    if (!allowDragDrop) return
    const ref = readCanvasReferenceFromDrop(e.dataTransfer)
    if (!ref || !onDropReference) return
    e.preventDefault()
    onDropReference(ref)
  }

  return (
    <div
      data-reference-strip=""
      className={cn(
        "it2tv-scrollbar -mx-1 overflow-x-auto px-1",
        !scrollLayout && "md:overflow-visible",
        highlightDrag && "rounded-lg bg-ui-accent/10 ring-1 ring-ui-accent/30",
      )}
      aria-label={ariaLabel ?? t("canvas.panel.referencesTitle")}
      title={fileDropEnabled ? t("canvas.panel.dropFilesHint") : undefined}
      onDragLeave={clearDragState}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className={cn("flex w-max min-w-full flex-nowrap gap-2", !scrollLayout && "md:w-auto md:flex-wrap")}>
        {references.map((ref, index) => (
          <div key={ref.key} className={cn("flex flex-col gap-1", ref.muted && "opacity-40")}>
            <ReferenceInputThumb
              url={ref.url}
              thumbUrl={ref.thumbUrl}
              mediaType={ref.mediaType === "video" ? "video" : ref.mediaType === "audio" ? "audio" : "image"}
              onOpenPreview={() => {
                if (ref.mediaType !== "video" && ref.mediaType !== "audio" && onOpenPreview) {
                  onOpenPreview(index)
                  return
                }
                setLightbox(ref)
              }}
              onRemove={!ref.disableToggle && onRemove ? () => onRemove(index) : undefined}
              cornerAction={ref.disableToggle ? "visibility" : "remove"}
              visible={ref.enabled !== false}
              onToggleVisibility={ref.disableToggle && onToggleEnabled ? () => onToggleEnabled(index) : undefined}
              visibilityOnAriaLabel={visibilityOnAriaLabel}
              visibilityOffAriaLabel={visibilityOffAriaLabel}
              onApply={onApply ? () => onApply(index) : undefined}
              onSketchEdit={
                onSketchEdit && ref.mediaType === "image" && ref.url ? () => onSketchEdit(index) : undefined
              }
              sketchEditing={sketchEditingKey === ref.key}
              sketchEditingLabel={t("canvas.sketch.correctionShort")}
              openPreviewTitle={t("canvas.panel.previewReference")}
              removeAriaLabel={t("canvas.panel.removeReference")}
              applyAriaLabel={applyTitle}
              sketchEditAriaLabel={t("canvas.sketch.editInSketch")}
            />
            {ref.label && onLabelClick ? (
              <button
                type="button"
                title={ref.label}
                onClick={() => onLabelClick(index)}
                className="max-w-[7.5rem] truncate rounded bg-ui-surface-raised px-1 py-0.5 text-center text-[10px] text-ui-text-primary hover:bg-ui-bg-elevated hover:text-ui-accent-text"
              >
                {ref.label}
              </button>
            ) : null}
            {onInsertMention && ref.label && !onLabelClick ? (
              <button
                type="button"
                title={t("canvas.panel.insertMention")}
                onClick={() => onInsertMention(ref.label!, index)}
                className="max-w-[7.5rem] truncate rounded bg-ui-surface-raised px-1 py-0.5 text-center font-mono text-[10px] text-ui-accent-text hover:bg-ui-surface-raised"
              >
                {ref.label}
              </button>
            ) : null}
          </div>
        ))}
        {showAddTile && onAddClick ? (
          <AddDashedTile
            buttonRef={addButtonRef}
            onClick={onAddClick}
            uploading={adding}
            ariaLabel={t("canvas.referencesModal.title")}
            ariaPressed={referencePickerOpen}
            className={cn(
              referencePickerOpen
                ? "border-ui-accent bg-ui-accent/10 text-ui-link"
                : "border-ui-border-strong text-ui-text-muted hover:border-ui-accent hover:text-ui-link",
            )}
          />
        ) : null}
        {trailing}
      </div>

      <SketchDialog
        open={lightbox != null && lightbox.mediaType === "audio" && Boolean(lightbox.url)}
        onOpenChange={(open) => {
          if (!open) setLightbox(null)
        }}
        projectId={projectId ?? 0}
        sourceUrl={null}
        audioUrl={lightbox?.mediaType === "audio" ? lightbox.url : null}
        presentation="view"
        allowEdit={false}
        persist="none"
        nested
        headerTitle={lightbox?.label ?? null}
      />
      <ImageLightboxDialog
        open={
          lightbox != null &&
          lightbox.mediaType !== "audio" &&
          Boolean(lightbox.mediaType === "video" ? lightbox.url : lightboxViewUrl(lightbox))
        }
        onOpenChange={(open) => {
          if (!open) setLightbox(null)
        }}
        url={lightbox?.mediaType === "video" ? null : lightbox ? lightboxViewUrl(lightbox) : null}
        videoUrl={lightbox?.mediaType === "video" ? lightbox.url : null}
        title={lightbox?.label ?? null}
        downloadUrl={lightbox?.mediaType === "video" ? null : lightbox ? lightboxDownloadUrl(lightbox) : null}
        downloadFilename={lightbox?.label ? `${lightbox.label}.png` : null}
        copyable={lightbox?.mediaType !== "video" && Boolean(lightbox && lightboxDownloadUrl(lightbox))}
        nested
      />
    </div>
  )
}
